import { Fragment, useEffect, useState } from 'react';
import { api, money, entityRange } from '../api.js';
import { ReportHead, BalanceRule, Money, Empty } from '../components/Shell.jsx';
import { ViewLink } from '../components/ViewLink.jsx';

/** Shared date-range controls. Every statement takes a period. */
function RangeControls({ range, setRange, onRun, busy, mode = 'range', extra }) {
  return (
    <div className="controls">
      {mode === 'range' ? (
        <>
          <label className="field">
            <span>From</span>
            <input type="date" value={range.from}
                   onChange={(e) => setRange({ ...range, from: e.target.value })} />
          </label>
          <label className="field">
            <span>To</span>
            <input type="date" value={range.to}
                   onChange={(e) => setRange({ ...range, to: e.target.value })} />
          </label>
        </>
      ) : (
        <label className="field">
          <span>As at</span>
          <input type="date" value={range.to}
                 onChange={(e) => setRange({ ...range, to: e.target.value })} />
        </label>
      )}
      {extra}
      <button className="btn" onClick={onRun} disabled={busy}>
        {busy ? 'Running' : 'Run'}
      </button>
    </div>
  );
}

/**
 * `initialRange` is for a screen that was OPENED from another one — a drill-down
 * arrives carrying the period the first report was run for, and the ledger has
 * to answer for that period rather than for the entity's default year, or the
 * figure that was clicked will not be the figure that foots.
 */
function useReport(entity, path, buildQuery, initialRange = null) {
  const [range, setRange] = useState(() => initialRange ?? entityRange(entity));
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (r = range) => {
    setBusy(true); setError(null);
    try {
      setData(await api.get(`/api/reports/${entity.id}/${path}?${buildQuery(r)}`));
    } catch (e) {
      setError(e.message); setData(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { if (entity?.id) run(); /* eslint-disable-next-line */ }, [entity?.id]);

  return { range, setRange, data, error, busy, run };
}

/* ------------------------------------------------------------ 1. Trial balance */
export function TrialBalance({ entity, onOpenAccount }) {
  const r = useReport(entity, 'trial-balance', (x) => `from=${x.from}&to=${x.to}`);

  // Every figure on a trial balance is the same question — "what is IN that
  // account?" — and the answer is the ledger for it. The period comes off the
  // RUN (`r.data.from/to`), not off the date boxes, because those can have been
  // edited since without pressing Run; taking them would open a ledger for a
  // period that never produced the balance being asked about.
  const open = onOpenAccount && ((row) => onOpenAccount({
    accountId: row.account_id, from: r.data.from, to: r.data.to,
  }));

  return (
    <>
      <ReportHead entity={entity} title="Trial balance"
                  sub="Opening balance, movement for the period, and closing balance for every account with activity." />
      <RangeControls range={r.range} setRange={r.setRange} onRun={() => r.run()} busy={r.busy} />
      {r.error && <div className="error">{r.error}</div>}

      {r.data && (r.data.rows.length === 0 ? (
        <Empty><p>Nothing was posted in this period.</p><p>Capture a journal or an invoice to see it here.</p></Empty>
      ) : (
        <>
          <div className="scroll-x">
            <table className="ledger">
              <thead>
                <tr>
                  <th style={{ width: 70 }}>Code</th>
                  <th>Account</th>
                  <th className="num">Opening</th>
                  <th className="num col-debit">Debit</th>
                  <th className="num col-credit">Credit</th>
                  <th className="num">Net</th>
                  <th className="num">Closing</th>
                </tr>
              </thead>
              <tbody>
                {r.data.rows.map((row) => (
                  // The whole row is clickable because that is where the eye
                  // already is — but the row is not the control. A <tr> given a
                  // role and a tabindex stops being a row to a screen reader,
                  // so the account name is a real button and the row click just
                  // forwards to it.
                  <tr key={row.account_id}
                      className={open ? 'row--drill' : undefined}
                      onClick={open ? () => open(row) : undefined}>
                    <td className="code figure">{row.account_code}</td>
                    <td>
                      {open ? (
                        <ViewLink className="cell-link" view="general-ledger" entityId={entity.id}
                                  arg={{ accountId: row.account_id, from: r.data.from, to: r.data.to }}
                                  onClick={(e) => e.stopPropagation()}
                                  onNavigate={() => open(row)}>
                          {row.account_name}
                        </ViewLink>
                      ) : row.account_name}
                    </td>
                    <td className="num"><Money value={row.opening} /></td>
                    <td className="num col-debit figure">{row.debit === '0.00' ? '—' : money(row.debit)}</td>
                    <td className="num col-credit figure">{row.credit === '0.00' ? '—' : money(row.credit)}</td>
                    <td className="num"><Money value={row.net} /></td>
                    <td className="num"><Money value={row.closing} /></td>
                  </tr>
                ))}
              </tbody>
              {/* Seven cells, matching the seven headers: colSpan={2} for
                  Code and Account, then one each for Opening, Debit, Credit,
                  Net and Closing. Every figure here is the server's — the
                  client never adds money up (web/src/api.js). */}
              <tfoot>
                <tr>
                  <td colSpan={2}>Total</td>
                  <td className="num figure">{money(r.data.totals.opening)}</td>
                  <td className="num col-debit figure">{money(r.data.totals.debit)}</td>
                  <td className="num col-credit figure">{money(r.data.totals.credit)}</td>
                  <td className="num figure">{money(r.data.totals.net)}</td>
                  <td className="num figure">{money(r.data.totals.closing)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <BalanceRule difference={r.data.totals.outOfBalance}
                       context={<>Debits of <strong>{money(r.data.totals.debit)}</strong> equal credits of <strong>{money(r.data.totals.credit)}</strong>.</>} />
        </>
      ))}
    </>
  );
}

/* -------------------------------------------------------- 2. Income statement */
export function IncomeStatement({ entity }) {
  const r = useReport(entity, 'income-statement', (x) => `from=${x.from}&to=${x.to}`);
  const s = r.data?.subtotals;

  const section = (label, subtypes) => {
    const rows = (r.data?.rows ?? []).filter((x) => subtypes.includes(x.account_subtype));
    if (rows.length === 0) return null;
    return (
      <>
        <tr className="group"><td colSpan={3}>{label}</td></tr>
        {rows.map((row) => (
          <tr key={row.account_id}>
            <td className="code figure">{row.account_code}</td>
            <td>{row.account_name}</td>
            <td className="num"><Money value={row.amount} /></td>
          </tr>
        ))}
      </>
    );
  };

  const total = (label, value) => (
    <tr className="subtotal">
      <td /><td>{label}</td><td className="num"><Money value={value} /></td>
    </tr>
  );

  return (
    <>
      <ReportHead entity={entity} title="Income statement"
                  sub="Revenue and expenses for the period, down to profit." />
      <RangeControls range={r.range} setRange={r.setRange} onRun={() => r.run()} busy={r.busy} />
      {r.error && <div className="error">{r.error}</div>}

      {r.data && (r.data.rows.length === 0 ? (
        <Empty><p>No income or expenses in this period.</p></Empty>
      ) : (
        <>
          <div className="scroll-x">
            <table className="ledger">
              <thead>
                <tr><th style={{ width: 70 }}>Code</th><th>Account</th><th className="num" style={{ width: 160 }}>Amount</th></tr>
              </thead>
              <tbody>
                {section('Revenue', ['revenue'])}
                {section('Cost of sales', ['cost_of_sales'])}
                {total('Gross profit', s.grossProfit)}
                {section('Other income', ['other_income'])}
                {section('Operating expenses', ['operating_expense'])}
                {total('Operating profit', s.operatingProfit)}
                {section('Finance costs', ['finance_cost'])}
                {section('Taxation', ['tax_expense'])}
              </tbody>
              <tfoot>
                <tr><td /><td>Profit for the period</td><td className="num"><Money value={s.profitForPeriod} /></td></tr>
              </tfoot>
            </table>
          </div>
          <div className="note">
            Prepared on the accrual basis in the presentation order of IFRS for SMEs.
            Amounts are shown as positive figures; a loss appears in brackets.
          </div>
        </>
      ))}
    </>
  );
}

/* ------------------------------------------------------------ 3. Balance sheet */
export function BalanceSheet({ entity }) {
  const r = useReport(entity, 'balance-sheet', (x) => `asAt=${x.to}`);
  const t = r.data?.totals;

  const section = (label, type, subtypes) => {
    const rows = (r.data?.rows ?? []).filter(
      (x) => x.account_type === type && (!subtypes || subtypes.includes(x.account_subtype)));
    if (rows.length === 0) return null;
    return (
      <>
        <tr className="group"><td colSpan={3}>{label}</td></tr>
        {rows.map((row, i) => (
          <tr key={row.account_id ?? `computed-${i}`} className={row.is_computed ? 'computed' : ''}>
            <td className="code figure">{row.is_computed ? '' : row.account_code}</td>
            <td className="name">{row.account_name}</td>
            <td className="num"><Money value={row.amount} /></td>
          </tr>
        ))}
      </>
    );
  };

  return (
    <>
      <ReportHead entity={entity} title="Balance sheet"
                  sub="Assets, liabilities and equity at a date." />
      <RangeControls mode="asAt" range={r.range} setRange={r.setRange} onRun={() => r.run()} busy={r.busy} />
      {r.error && <div className="error">{r.error}</div>}

      {r.data && (r.data.rows.length === 0 ? (
        <Empty><p>Nothing has been posted yet.</p><p>Start with opening balances.</p></Empty>
      ) : (
        <>
          <div className="scroll-x">
            <table className="ledger">
              <thead>
                <tr><th style={{ width: 70 }}>Code</th><th>Account</th><th className="num" style={{ width: 160 }}>{r.data.asAt}</th></tr>
              </thead>
              <tbody>
                {section('Non-current assets', 'asset', ['non_current_asset'])}
                {section('Current assets', 'asset', ['current_asset'])}
                <tr className="subtotal"><td /><td>Total assets</td><td className="num"><Money value={t.assets} /></td></tr>
                {section('Equity', 'equity')}
                {section('Non-current liabilities', 'liability', ['non_current_liability'])}
                {section('Current liabilities', 'liability', ['current_liability'])}
              </tbody>
              <tfoot>
                <tr><td /><td>Total equity and liabilities</td>
                    <td className="num"><Money value={t.equityAndLiabilities} /></td></tr>
              </tfoot>
            </table>
          </div>
          <BalanceRule difference={t.difference}
                       context={<>Total assets of <strong>{money(t.assets)}</strong> equal equity plus liabilities.</>} />
          <div className="note">
            Profit for the year to date is shown as a computed line. It moves to retained
            earnings when the year is closed, so this statement balances every month rather
            than only at year end.
          </div>
        </>
      ))}
    </>
  );
}

/* ------------------------------------------------------------ 4. General ledger */
/**
 * `arriving` is set when this screen was opened by a drill-down rather than from
 * the rail: `{ accountId, from, to }`. It is read ONCE, at mount, and stays
 * correct only because App's remount key includes the arriving account as well
 * as the screen and the company. Read that comment before changing this one:
 * the case it exists for is the rail's "General ledger" pressed while a
 * drilled-down ledger is on screen, where the view name does not change and
 * nothing here would otherwise notice the filter had been dropped.
 *
 * `onOpenAccount` is the SAME channel the trial balance uses, given to the same
 * screen: this ledger's contra column drills to another account's ledger, which
 * is this screen again with a different account. It is a prop rather than a
 * local `setAccountId` on purpose — the account is part of App's remount key
 * and of the URL, so a drill done in here would leave the address bar and the
 * back button pointing at the account you came FROM.
 */
export function GeneralLedger({ entity, arriving = null, onOpenAccount = null }) {
  const [accountId, setAccountId] = useState(arriving?.accountId ?? '');
  const [accounts, setAccounts] = useState([]);
  // Which SPLIT row is expanded, and the lines behind the entries already
  // fetched. Keyed by the row rather than by the entry: with no account filter
  // on, every leg of a split entry is its own row, and keying by entry_id would
  // open all of them at once.
  const [openSplit, setOpenSplit] = useState(null);
  const [entryLines, setEntryLines] = useState({});   // entry_id -> lines
  const r = useReport(entity, 'general-ledger', (x) =>
    `from=${x.from}&to=${x.to}${accountId ? `&accountId=${accountId}` : ''}`,
    arriving?.from && arriving?.to ? { from: arriving.from, to: arriving.to } : null);

  useEffect(() => {
    if (!entity?.id) return;
    api.get(`/api/entities/${entity.id}/accounts`)
      .then((d) => setAccounts(d.accounts.filter((a) => a.is_postable)))
      .catch(() => setAccounts([]));
  }, [entity?.id]);

  // The period comes off the RUN, not off the date boxes, for the reason the
  // trial balance's drill-down does the same: those can have been edited since
  // without pressing Run, and the ledger would open on a period that never
  // produced the line that was clicked.
  const openContra = onOpenAccount && ((row) => onOpenAccount({
    accountId: row.contra_account_id, from: r.data.from, to: r.data.to,
  }));

  /**
   * A split has no contra to drill to, so it drills to the ENTRY — expanded in
   * place, the way the VAT report opens the entries behind a tax code. That is
   * the honest destination: the question "what is the other side of this?" is
   * answered by all of the other sides, and there is no screen that shows one
   * journal entry on its own to send the reader to instead.
   */
  const toggleSplit = async (key, entryId) => {
    if (openSplit === key) { setOpenSplit(null); return; }
    setOpenSplit(key);
    if (entryLines[entryId]) return;
    try {
      const d = await api.get(`/api/entities/${entity.id}/journals/${entryId}`);
      setEntryLines((cur) => ({ ...cur, [entryId]: d.lines }));
    } catch {
      // An empty array is "we asked and got nothing"; undefined is "we have not
      // asked yet", and the rows below tell the two apart.
      setEntryLines((cur) => ({ ...cur, [entryId]: [] }));
    }
  };

  let lastAccount = null;

  return (
    <>
      <ReportHead entity={entity} title="General ledger"
                  sub="Every posted line, in date order, with its contra account and a running balance." />
      <RangeControls range={r.range} setRange={r.setRange} onRun={() => r.run()} busy={r.busy}
        extra={
          <label className="field">
            <span>Account</span>
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">All accounts</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </select>
          </label>
        } />
      {r.error && <div className="error">{r.error}</div>}

      {r.data && (r.data.rows.length === 0 ? (
        <Empty><p>No postings in this period.</p></Empty>
      ) : (
        <div className="scroll-x">
          <table className="ledger">
            <thead>
              <tr>
                <th style={{ width: 60 }}>Entry</th>
                <th style={{ width: 100 }}>Date</th>
                <th>Description</th>
                {/* Beside the narrative and before the contact, exactly where
                    the cash book listing puts its own contra column. */}
                <th>Contra</th>
                <th>Contact</th>
                <th className="num col-debit">Debit</th>
                <th className="num col-credit">Credit</th>
                <th className="num">Balance</th>
              </tr>
            </thead>
            <tbody>
              {r.data.rows.map((row, i) => {
                const header = row.account_code !== lastAccount;
                lastAccount = row.account_code;
                const key = `${row.account_code}-${i}`;
                const lines = entryLines[row.entry_id];
                return (
                  <Fragment key={key}>
                    {header && (
                      <tr className="group">
                        <td colSpan={8}>{row.account_code} — {row.account_name}</td>
                      </tr>
                    )}
                    <tr>
                      <td className="code figure">{row.entry_no}</td>
                      <td className="figure">{row.transaction_date}</td>
                      <td>{row.description}</td>
                      {/* THE COLUMN NEVER NAMES AN ACCOUNT THE ENTRY DID NOT.
                          A two-line entry has one other side and this is it; an
                          entry with three legs — every VAT-bearing cash book
                          line is one — has no single other side, so it says so
                          and opens the whole entry instead. */}
                      <td>
                        {row.is_split ? (
                          <button type="button" className="cell-link"
                                  onClick={() => toggleSplit(key, row.entry_id)}
                                  aria-expanded={openSplit === key}>
                            —split—
                          </button>
                        ) : openContra ? (
                          <ViewLink className="cell-link" view="general-ledger" entityId={entity.id}
                                    arg={{ accountId: row.contra_account_id, from: r.data.from, to: r.data.to }}
                                    onNavigate={() => openContra(row)}>
                            <span className="code figure">{row.contra_code}</span> — {row.contra_name}
                          </ViewLink>
                        ) : (
                          <><span className="code figure">{row.contra_code}</span> — {row.contra_name}</>
                        )}
                      </td>
                      <td>{row.counterparty_name ?? ''}</td>
                      <td className="num col-debit figure">{row.debit === '0.00' ? '—' : money(row.debit)}</td>
                      <td className="num col-credit figure">{row.credit === '0.00' ? '—' : money(row.credit)}</td>
                      <td className="num"><Money value={row.running_balance} /></td>
                    </tr>
                    {openSplit === key && (
                      lines === undefined ? (
                        <tr><td colSpan={8} className="detail-note">Loading…</td></tr>
                      ) : lines.length === 0 ? (
                        <tr><td colSpan={8} className="detail-note">
                          That entry could not be opened.
                        </td></tr>
                      ) : lines.map((l) => (
                        <tr key={l.id} className="detail">
                          <td className="code figure">{l.line_no}</td>
                          {/* colSpan over Date, Description, Contra and Contact:
                              the split's legs are all one entry on one date, and
                              what a reader needs is which account took what. */}
                          <td colSpan={4}>
                            <span className="code figure">{l.account_code}</span> — {l.account_name}
                            {l.description && <span className="detail-who"> · {l.description}</span>}
                            {l.counterparty_name && <span className="detail-who"> · {l.counterparty_name}</span>}
                          </td>
                          <td className="num col-debit figure">{l.debit ? money(l.debit) : '—'}</td>
                          <td className="num col-credit figure">{l.credit ? money(l.credit) : '—'}</td>
                          <td />
                        </tr>
                      ))
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}

/* ---------------------------------------------------------------- 5. VAT report */
export function VatReport({ entity }) {
  const r = useReport(entity, 'vat', (x) => `from=${x.from}&to=${x.to}`);
  const s = r.data?.summary;

  // DECLARED ABOVE the not-registered early return, not beside the table they
  // serve: React requires every hook to run on every render, and an entity
  // that is not VAT registered returns before the table exists.
  const [openCode, setOpenCode] = useState(null);
  const [detail, setDetail] = useState({});   // tax_code_id -> rows

  /**
   * Expand in place rather than navigating away: the summary is the context
   * for the detail, and a reviewer signing a return is reading the two
   * against each other. The fetched rows are kept, so collapsing and
   * reopening a code costs nothing.
   */
  const toggle = async (row) => {
    if (openCode === row.tax_code_id) { setOpenCode(null); return; }
    setOpenCode(row.tax_code_id);
    if (detail[row.tax_code_id]) return;
    try {
      const d = await api.get(
        `/api/reports/${entity.id}/vat/${row.tax_code_id}?from=${r.data.from}&to=${r.data.to}`);
      setDetail((cur) => ({ ...cur, [row.tax_code_id]: d.rows }));
    } catch {
      // An empty array is the "we asked and got nothing" state; undefined is
      // "we have not asked yet", and the row below distinguishes them.
      setDetail((cur) => ({ ...cur, [row.tax_code_id]: [] }));
    }
  };

  if (entity && !entity.is_vat_registered) {
    return (
      <>
        <ReportHead entity={entity} title="VAT report" />
        <Empty>
          <p>{entity.name} is not registered for VAT.</p>
          <p>Register the client and set a VAT category to produce a return.</p>
        </Empty>
      </>
    );
  }

  return (
    <>
      <ReportHead entity={entity} title="VAT report"
                  sub={`Output and input tax for the period, in VAT201 order. Category ${entity?.vat_category ?? '—'}.`} />
      <RangeControls range={r.range} setRange={r.setRange} onRun={() => r.run()} busy={r.busy} />
      {r.error && <div className="error">{r.error}</div>}

      {r.data && (r.data.rows.length === 0 ? (
        <Empty><p>No VAT transactions in this period.</p></Empty>
      ) : (
        <>
          {/* Each tile leads with the taxable VALUE and carries its tax
              beneath. A tile headed "output tax" beside one headed "payable to
              SARS" showing the same figure reads as though the first is the
              second; leading with turnover makes the two plainly different
              questions, and VAT201 boxes 1 and 14 are values in any case. */}
          <div className="stat-row">
            <div className="stat">
              <div className="stat__label">Turnover, standard and zero rated</div>
              <div className="stat__value">{money(s.outputTaxable)}</div>
              <div className="stat__sub">Output tax {money(s.outputVat)}</div>
            </div>
            <div className="stat">
              <div className="stat__label">Expenses, input tax claimable</div>
              <div className="stat__value">{money(s.inputTaxable)}</div>
              <div className="stat__sub">Input tax {money(s.claimableInputVat)}</div>
            </div>
            <div className="stat">
              <div className="stat__label">{Number(s.netPayable.replace(/[^\d.-]/g,'')) < 0 ? 'Refundable' : 'Payable to SARS'}</div>
              <div className="stat__value">{money(s.netPayable)}</div>
              <div className="stat__sub">Output less input tax</div>
            </div>
            <div className="stat">
              <div className="stat__label">Expenses, input tax denied</div>
              <div className="stat__value stat__value--credit">{money(s.deniedTaxable)}</div>
              <div className="stat__sub">Denied tax {money(s.deniedInputVat)}</div>
            </div>
          </div>

          <div className="scroll-x">
            <table className="ledger">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>Code</th><th>Description</th>
                  <th style={{ width: 90 }}>Box</th>
                  <th className="num">Taxable value</th>
                  <th className="num">VAT</th>
                </tr>
              </thead>
              <tbody>
                {r.data.rows.map((row) => (
                  <Fragment key={row.tax_code_id}>
                    <tr className="row--drill" onClick={() => toggle(row)}>
                      <td className="code figure">{row.code}</td>
                      <td>
                        <button type="button" className="cell-link"
                                onClick={(e) => { e.stopPropagation(); toggle(row); }}
                                aria-expanded={openCode === row.tax_code_id}>
                          {row.name}
                        </button>
                        {row.is_input_denied && <span className="neg"> · not claimable</span>}
                      </td>
                      <td className="figure code">{row.vat201_box ?? '—'}</td>
                      <td className="num"><Money value={row.taxable} /></td>
                      <td className="num"><Money value={row.vat} /></td>
                    </tr>
                    {openCode === row.tax_code_id && (
                      detail[row.tax_code_id] === undefined ? (
                        <tr><td colSpan={5} className="detail-note">Loading…</td></tr>
                      ) : detail[row.tax_code_id].length === 0 ? (
                        <tr><td colSpan={5} className="detail-note">No entries behind this figure.</td></tr>
                      ) : detail[row.tax_code_id].map((d) => (
                        <tr key={d.entry_id} className="detail">
                          <td className="code figure">{d.entry_no}</td>
                          {/* colSpan over Description AND Box: the box number
                              belongs to the tax code, not to the entries under
                              it, and a counterparty forced into a 90px column
                              wraps to three lines and makes the detail harder
                              to read than the figure it is explaining. */}
                          <td colSpan={2}>
                            {d.transaction_date} · {d.entry_description}
                            {d.counterparty_name &&
                              <span className="detail-who"> · {d.counterparty_name}</span>}
                          </td>
                          <td className="num"><Money value={d.taxable} /></td>
                          <td className="num"><Money value={d.vat} /></td>
                        </tr>
                      ))
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>

          <div className="note note--warn">
            {r.data.note} Denied amounts are posted to the expense, so the cost recorded
            in the income statement is the full amount paid.
          </div>
          <div className="note">
            This report produces the figures for a VAT201. It does not submit to SARS —
            there is no third-party submission channel — so the return is still captured
            on eFiling.
          </div>
        </>
      ))}
    </>
  );
}

/* ------------------------------------------------- 6 & 7. Age analysis */
export function AgeAnalysis({ entity, type }) {
  const label = type === 'customer' ? 'Debtors' : 'Creditors';
  const r = useReport(entity, `age-analysis/${type}`, (x) => `asAt=${x.to}`);
  const t = r.data?.totals;

  return (
    <>
      <ReportHead entity={entity} title={`${label} age analysis`}
                  sub={type === 'customer'
                    ? 'What each customer owes, by age of the invoice.'
                    : 'What is owed to each supplier, by age of the invoice.'} />
      <RangeControls mode="asAt" range={r.range} setRange={r.setRange} onRun={() => r.run()} busy={r.busy} />
      {r.error && <div className="error">{r.error}</div>}

      {r.data && (r.data.rows.length === 0 ? (
        <Empty>
          <p>Nothing outstanding at this date.</p>
          <p>{type === 'customer' ? 'Every invoice has been settled.' : 'Every supplier invoice has been paid.'}</p>
        </Empty>
      ) : (
        <>
          <div className="scroll-x">
            <table className="ledger">
              <thead>
                <tr>
                  <th style={{ width: 80 }}>Code</th><th>{type === 'customer' ? 'Customer' : 'Supplier'}</th>
                  <th className="num">Current</th><th className="num">30 days</th>
                  <th className="num">60 days</th><th className="num">90 days</th>
                  <th className="num">120+ days</th><th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {r.data.rows.map((row) => (
                  <tr key={row.counterparty_id}>
                    <td className="code figure">{row.counterparty_code}</td>
                    <td>{row.counterparty_name}</td>
                    <td className="num"><Money value={row.current} /></td>
                    <td className="num"><Money value={row.days_30} /></td>
                    <td className="num"><Money value={row.days_60} /></td>
                    <td className="num"><Money value={row.days_90} /></td>
                    <td className="num"><Money value={row.days_120} /></td>
                    <td className="num"><Money value={row.total} /></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={2}>Total</td>
                  <td className="num figure">{money(t.current)}</td>
                  <td className="num figure">{money(t.days_30)}</td>
                  <td className="num figure">{money(t.days_60)}</td>
                  <td className="num figure">{money(t.days_90)}</td>
                  <td className="num figure">{money(t.days_120)}</td>
                  <td className="num figure">{money(t.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="note">
            Ageing is computed from allocations, not from a running balance, so this total
            agrees with the {type === 'customer' ? 'debtors' : 'creditors'} control account
            in the general ledger.
          </div>
        </>
      )) }
    </>
  );
}

/* ------------------------------------------------- 8. Cash book listing */
/**
 * What a cash book put through the ledger, per bank account, per period.
 *
 * ⚠ NOT the proof sheet. Pastel's `Preview…`/`Print…` prints a PRE-post sheet
 * from the batch being captured — totals per tax code and the reasons a line
 * will not post — and that belongs to the cash book capture screen. This is the
 * post-hoc listing: it reads the JOURNAL back, so a batch that was abandoned
 * appears nowhere in it and a line that posted appears whatever screen it was
 * captured on.
 *
 * The account dropdown is filtered to `is_bank` because that is the flag a cash
 * book may be opened against (decision 9) — not `control_kind`, which marks one
 * account per entity where a practice has several. Filtered in the browser off
 * the same account list the general ledger uses rather than through a second
 * endpoint: the server filters the REPORT to is_bank regardless, so a stale
 * dropdown can only offer an account that lists nothing.
 */
export function CashBookListing({ entity }) {
  const [bankAccountId, setBankAccountId] = useState('');
  const [banks, setBanks] = useState([]);
  const r = useReport(entity, 'cash-book', (x) =>
    `from=${x.from}&to=${x.to}${bankAccountId ? `&bankAccountId=${bankAccountId}` : ''}`);

  useEffect(() => {
    if (!entity?.id) return;
    api.get(`/api/entities/${entity.id}/accounts`)
      .then((d) => setBanks(d.accounts.filter((a) => a.is_bank)))
      .catch(() => setBanks([]));
  }, [entity?.id]);

  let lastBank = null;

  return (
    <>
      <ReportHead entity={entity} title="Cash book listing"
                  sub="Every posted cash book line against a bank account, with its contra account, its VAT and a running balance." />
      <RangeControls range={r.range} setRange={r.setRange} onRun={() => r.run()} busy={r.busy}
        extra={
          <label className="field">
            <span>Cash book</span>
            <select value={bankAccountId} onChange={(e) => setBankAccountId(e.target.value)}>
              <option value="">All cash books</option>
              {banks.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
            </select>
          </label>
        } />
      {r.error && <div className="error">{r.error}</div>}

      {r.data && (r.data.rows.length === 0 ? (
        <Empty>
          <p>No cash book batches posted in this period.</p>
          <p>A batch that was abandoned moved nothing and is not listed.</p>
        </Empty>
      ) : (
        <>
          <div className="scroll-x">
            <table className="ledger">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Batch</th>
                  <th style={{ width: 100 }}>Date</th>
                  <th style={{ width: 90 }}>Reference</th>
                  <th>Description</th>
                  <th>Contra</th>
                  <th style={{ width: 50 }}>Tax</th>
                  <th className="num">Exclusive</th>
                  <th className="num">VAT</th>
                  <th className="num col-debit">Received</th>
                  <th className="num col-credit">Paid</th>
                  <th className="num">Balance</th>
                </tr>
              </thead>
              <tbody>
                {r.data.rows.map((row, i) => {
                  const header = row.bank_code !== lastBank;
                  lastBank = row.bank_code;
                  // A subledger line names a person; a GL line names an
                  // account. Pastel's single Account column, which changes what
                  // it looks up with the GCS kind, read back the same way.
                  const contra = row.contra_kind === 'ledger'
                    ? `${row.contra_code ?? ''} — ${row.contra_name ?? ''}`
                    : (row.counterparty_name ?? '');
                  return (
                    <Fragment key={`${row.entry_no}-${row.bank_code}-${i}`}>
                      {header && (
                        <tr className="group">
                          <td colSpan={11}>{row.bank_code} — {row.bank_name}</td>
                        </tr>
                      )}
                      <tr>
                        <td className="code figure">
                          {row.batch_number}{row.line_no === null ? '' : `/${row.line_no}`}
                        </td>
                        <td className="figure">{row.transaction_date}</td>
                        <td className="figure">{row.reference ?? ''}</td>
                        <td>{row.description}</td>
                        <td>{contra}</td>
                        <td className="code figure">{row.tax_code ?? ''}</td>
                        <td className="num figure">{money(row.excl)}</td>
                        <td className="num figure">{row.vat === '0.00' ? '—' : money(row.vat)}</td>
                        <td className="num col-debit figure">
                          {row.receipt === '0.00' ? '—' : money(row.receipt)}
                        </td>
                        <td className="num col-credit figure">
                          {row.payment === '0.00' ? '—' : money(row.payment)}
                        </td>
                        <td className="num"><Money value={row.running_balance} /></td>
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
              {/* Eleven cells, matching the eleven headers. Every figure is the
                  server's — the client displays money and never adds it up. */}
              <tfoot>
                <tr>
                  <td colSpan={6}>Total — {r.data.totals.lines} line{r.data.totals.lines === 1 ? '' : 's'}</td>
                  <td className="num figure">{money(r.data.totals.excl)}</td>
                  <td className="num figure">{money(r.data.totals.vat)}</td>
                  <td className="num col-debit figure">{money(r.data.totals.receipts)}</td>
                  <td className="num col-credit figure">{money(r.data.totals.payments)}</td>
                  <td className="num figure">{money(r.data.totals.net)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Per cash book, because the running balance is per cash book and a
              single total across several of them is a different number: a
              transfer between two of the practice's own cash books is a payment
              on one and a receipt on the other, so it cancels in the total
              above and appears in full in both rows here. */}
          {r.data.books.length > 1 && (
            <div className="scroll-x">
              <table className="ledger">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>Code</th><th>Cash book</th>
                    <th className="num col-debit">Received</th>
                    <th className="num col-credit">Paid</th>
                    <th className="num">Net</th>
                    <th className="num">Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {r.data.books.map((b) => (
                    <tr key={b.accountId}>
                      <td className="code figure">{b.code}</td>
                      <td>{b.name}</td>
                      <td className="num col-debit figure">{money(b.receipts)}</td>
                      <td className="num col-credit figure">{money(b.payments)}</td>
                      <td className="num"><Money value={b.net} /></td>
                      <td className="num"><Money value={b.closing} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="note">
            Posted cash book entries only. The running balance opens on the account's whole
            balance from every source, so its closing figure equals the general ledger's only
            where nothing but the cash book moved that account in the period — a difference is
            a payment, slip or journal captured on another screen, not an error.
          </div>
        </>
      ))}
    </>
  );
}

/* --------------------------------------------- 9. Bank reconciliation statement */
/**
 * The report a bookkeeper signs, and the milestone's flagship invariant printed
 * rather than a second thing that might agree with it:
 *
 *   (B_close − B_open) − Σ(unmatched ledger lines in the period)
 *         = (S_close − S_open) − Σ(unmatched statement lines)
 *
 * ⚠ BOTH UNMATCHED SETS, and that is the whole reason this screen has two
 * lists. The plan's FIRST version of the invariant named only the statement's,
 * which makes an unpresented cheque — a movement in the books that the bank has
 * not shown — invisible: the equation balances while the reconciliation is
 * wrong, and surfacing exactly that is what a reconciliation is FOR. 057's
 * header carries the correction in full, including why the equation must be
 * DIFFERENCED so that it never requires the ledger and the statement to open on
 * the same balance. An entity mid-history carries timing differences from
 * before this statement, and demanding they agree fails a CORRECT first
 * reconciliation.
 *
 * ⚠ NOT the reconciliation SCREEN, which is where matches are approved. This is
 * the post-hoc statement: it reads the decisions back and prints what they add
 * up to. The two answer different questions and neither is the other's summary.
 *
 * Every figure is the server's. The client displays money and never does
 * arithmetic on it — there is no place in this component where two rands are
 * added, and the difference is compared as the server's own cents string.
 */
export function ReconciliationStatement({ entity }) {
  const [statementId, setStatementId] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  // Keyed on the statement rather than on a date range: a reconciliation is
  // about one imported file, whose period is the file's own. The picker list
  // arrives on the same response, so the first load draws the whole page.
  useEffect(() => {
    if (!entity?.id) return;
    let live = true;
    setBusy(true); setError(null);
    api.get(`/api/reports/${entity.id}/bank-reconciliation${statementId ? `?statementId=${statementId}` : ''}`)
      .then((d) => { if (live) { setData(d); setError(null); } })
      .catch((e) => { if (live) { setError(e.message); setData(null); } })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [entity?.id, statementId]);

  const s = data?.statement ?? null;
  // The server's own answer, not a re-derivation: `reconciled` is computed from
  // difference_cents as a STRING, because parsing a BIGINT of cents through a
  // JavaScript number is the money-through-a-float bug this codebase refuses
  // everywhere else.
  const off = data ? data.reconciled === false : false;

  return (
    <>
      <ReportHead entity={entity} title="Bank reconciliation"
                  sub="What the ledger and the bank statement agree on, and every item they do not." />

      <div className="controls">
        <label className="field">
          <span>Statement</span>
          <select value={statementId} onChange={(e) => setStatementId(e.target.value)}>
            <option value="">Most recent</option>
            {(data?.statements ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.code} — {x.from} to {x.to} ({x.lines} line{x.lines === 1 ? '' : 's'})
              </option>
            ))}
          </select>
        </label>
        {busy && <span className="note">Running</span>}
      </div>

      {error && <div className="error">{error}</div>}

      {data && !s && (
        <Empty>
          <p>No bank statement has been imported for this company yet.</p>
          <p>Import one on the bank reconciliation screen — this statement reads its
             approved matches back and prints what they add up to.</p>
        </Empty>
      )}

      {s && (
        <>
          {/* The answer first, because "does it reconcile?" is the only
              question this page is opened to settle. Out of balance is a
              NORMAL state here and the wording says so — unlike a trial
              balance, where a difference means the ledger is broken. */}
          <div className={`balance${off ? ' balance--off' : ''}`}>
            <span className="balance__state">{off ? 'Not reconciled' : 'Reconciled'}</span>
            <span className="balance__detail">
              {off ? (
                <>Difference <strong>{money(s.difference)}</strong> — the two sides do not yet
                   account for the same movements. Every item making up the difference is listed
                   below; approve or capture them and run this again.</>
              ) : (
                <>Both sides account for {money(s.ledger_side)} of movement.
                   The {s.unmatched_ledger_lines} ledger item(s) and {s.unmatched_statement_lines}
                   {' '}statement item(s) below are timing differences, and they are listed rather
                   than hidden.</>
              )}
            </span>
          </div>

          <div className="scroll-x">
            <table className="ledger">
              <thead>
                <tr>
                  <th>{s.bank_code} — {s.bank_name}, {s.from_date} to {s.to_date}</th>
                  <th className="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr className="group"><td colSpan={2}>Per the ledger</td></tr>
                {/* ⚠ These two openings are NOT required to agree, and the
                    report does not ask them to. See the note below. */}
                <tr>
                  <td>Balance on the bank account the day before {s.from_date}</td>
                  <td className="num"><Money value={s.ledger_opening} /></td>
                </tr>
                <tr>
                  <td>Movement on the bank account in the period</td>
                  <td className="num"><Money value={s.ledger_movement} /></td>
                </tr>
                <tr>
                  <td>Balance on the bank account at {s.to_date}</td>
                  <td className="num"><Money value={s.ledger_closing} /></td>
                </tr>

                <tr className="group"><td colSpan={2}>Per the statement</td></tr>
                <tr>
                  <td>Opening balance</td>
                  <td className="num"><Money value={s.statement_opening} /></td>
                </tr>
                <tr>
                  <td>Movement over {s.statement_line_count} line{s.statement_line_count === 1 ? '' : 's'}</td>
                  <td className="num"><Money value={s.statement_movement} /></td>
                </tr>
                <tr>
                  <td>Closing balance</td>
                  <td className="num"><Money value={s.statement_closing} /></td>
                </tr>

                <tr className="group"><td colSpan={2}>What the two agree on</td></tr>
                <tr>
                  <td>Ledger movement in the period</td>
                  <td className="num"><Money value={s.ledger_movement} /></td>
                </tr>
                <tr>
                  <td>Less ledger items the statement does not show</td>
                  <td className="num"><Money value={s.unmatched_ledger} /></td>
                </tr>
                {s.matched_outside_period_cents !== '0' && (
                  <tr>
                    <td>Plus entries matched to this statement but dated outside the period</td>
                    <td className="num"><Money value={s.matched_outside_period} /></td>
                  </tr>
                )}
                <tr>
                  <td><strong>Movements the ledger accounts for</strong></td>
                  <td className="num"><Money value={s.ledger_side} /></td>
                </tr>
                <tr>
                  <td>Statement movement</td>
                  <td className="num"><Money value={s.statement_movement} /></td>
                </tr>
                <tr>
                  <td>Less statement items the ledger does not show</td>
                  <td className="num"><Money value={s.unmatched_statement} /></td>
                </tr>
                <tr>
                  <td><strong>Movements the statement accounts for</strong></td>
                  <td className="num"><Money value={s.statement_side} /></td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>Difference</td>
                  <td className="num"><Money value={s.difference} /></td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* ⚠ TWO LISTS, ALWAYS BOTH. The unpresented cheque lives in the
              first one and an invariant naming only the second cannot see it. */}
          <ItemList
            title="In the books, not on the statement"
            blurb="Movements captured in the period that this statement does not show — a cheque not yet presented, a batch posted after the statement date, or a capture that never happened at the bank."
            rows={(data.ledgerItems ?? []).filter((x) => x.kind === 'unmatched_ledger')}
            empty="Every movement on the bank account in this period appears on the statement."
            refHead="Entry" />

          <ItemList
            title="On the statement, not in the books"
            blurb="Lines the bank showed that nothing in the ledger accounts for — a consolidated fee, a debit order nobody captured, a deposit not yet written up. Capture them on the reconciliation screen."
            rows={data.statementItems ?? []}
            empty="Every line on this statement is accounted for in the ledger."
            refHead="Row in file" />

          {(data.ledgerItems ?? []).some((x) => x.kind === 'matched_outside_period') && (
            <ItemList
              title="Matched, but dated outside the statement period"
              blurb="A match may reach seven days either side of the line it pairs with, so an entry can be accounted for by this statement and fall outside the period it covers. These are added back above rather than absorbed, so the crossing is visible."
              rows={(data.ledgerItems ?? []).filter((x) => x.kind === 'matched_outside_period')}
              empty=""
              refHead="Entry" />
          )}

          <div className="note">
            Differenced over the period on purpose: this statement does <strong>not</strong> require
            the ledger and the bank to open on the same balance. An entity mid-history carries
            timing differences from before this statement, and demanding the two openings agree
            would fail a correct first reconciliation. A slip is reconciled by the clearing journal
            its approval posts — Dr slip suspense / Cr bank — and not by the suspense leg the match
            names, so that journal counts as accounted for here and so does its reversal if the
            approval was undone.
          </div>
        </>
      )}
    </>
  );
}

/** One list of reconciling items. Two of them are the report; the third is rare. */
function ItemList({ title, blurb, rows, empty, refHead }) {
  return (
    <div className="scroll-x">
      <table className="ledger">
        <thead>
          <tr><th colSpan={4}>{title}</th></tr>
          <tr>
            <th style={{ width: 100 }}>Date</th>
            <th style={{ width: 110 }}>{refHead}</th>
            <th>Description</th>
            <th className="num">Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={4}>{empty}</td></tr>
          ) : rows.map((x) => (
            <tr key={x.entry_id ? `${x.entry_id}-${x.line_no}` : x.statement_line_id}>
              <td className="figure">{x.transaction_date}</td>
              <td className="code figure">{x.reference}</td>
              <td>{x.description}</td>
              <td className="num"><Money value={x.amount} /></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="note">{blurb}</div>
    </div>
  );
}
